import { createFileRoute, useNavigate, ClientOnly } from '@tanstack/react-router'
import { useEffect } from 'react'
import * as v from 'valibot'
import { useAppStore } from '@/lib/app-store'
import { ApplicantRegisterForm } from './-components/applicants/register-form'
import { ApplicantFormSection } from './-components/applicants/applicant-form-section'
import { useSuspenseQuery } from '@tanstack/react-query'
import { firstIncompleteProfileStep, type ProfileUser } from 'shared/applicant-form'
import { usePostLoginRedirect } from '@/hooks/use-post-login-redirect'
import { trpc } from '@/main'
import { AuthCardSkeleton } from './-components/app-layout-skeleton'

export const Route = createFileRoute('/_app/register/applicant')({
  validateSearch: v.object({
    redirect: v.optional(v.string()),
    step: v.fallback(v.optional(v.picklist(['about', 'location', 'career'])), undefined),
    // Invitation token (employee confirming their registration from the email link).
    ref: v.optional(v.string()),
    // Set by the social-login callback when it couldn't link the company to the chosen account.
    // Only the social subset (SocialLinkErrorCode) ever arrives via the URL; the rest are set in-form.
    linkError: v.fallback(
      v.optional(v.picklist(['link-failed', 'link-personal-account', 'use-personal-account'])),
      undefined,
    ),
  }),
  component: RouteComponent,
  pendingComponent: () => <AuthCardSkeleton logo social terms />,
})

function RouteComponent() {
  const initialData = useAppStore((state) => state.store.applicantData)
  const callback = useAppStore((state) => state.store.callback)
  const { data: user, isFetching } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const navigate = useNavigate()
  const redirectAfterAuth = usePostLoginRedirect()
  const { step: initialStep, ref, linkError, redirect } = Route.useSearch()

  const isApplicant = user?.type === 'applicant'
  const isEmployee = user?.type === 'applicant' && user.isEmployee
  const allTestsPassed = !!user?.testPassed && !!user?.testPassedValues && !!user?.testPassedPersonality
  // First profile step still missing data; `null` = the profile is already complete (e.g. the API/SDK
  // supplied every field), so there's nothing left to ask and the wizard should be skipped entirely.
  const incompleteStep = isApplicant && !isEmployee && user ? firstIncompleteProfileStep(user as ProfileUser) : null

  // Send an applicant who hasn't finished the test to the test. Wait for the query to settle
  // (isFetching), not just the initial load: right after finishing the last test, keepPreviousData
  // still holds the pre-completion snapshot, so redirecting off it would bounce them back to /test.
  useEffect(() => {
    if (isFetching) return
    // Company-invited employees never fill the profile wizard — their data comes from the company.
    // Send them straight to their report once the tests are done, or to the tests if still pending.
    if (isEmployee) {
      navigate({ to: allTestsPassed ? '/report' : '/test' })
      return
    }
    // An applicant must finish the test before the profile wizard. Covers social sign-ups too.
    // Carry `redirect` so the onboarding chain keeps the visitor's intended post-auth destination.
    if (isApplicant && !allTestsPassed) {
      navigate({ to: '/test', search: { redirect } })
      return
    }
    // Tests done and the profile is already complete (API/SDK prefilled it): skip the wizard rather
    // than re-asking for fields the integration already provided, and go to the report.
    if (isApplicant && allTestsPassed && incompleteStep === null && user) {
      redirectAfterAuth(user)
    }
  }, [isFetching, isApplicant, isEmployee, allTestsPassed, incompleteStep, navigate, redirect, redirectAfterAuth, user])

  // Not logged in or not an applicant: show registration page (invitation confirm if a token is present)
  if (!user || !isApplicant) {
    return <ApplicantRegisterForm invitationToken={ref} linkError={linkError} />
  }

  // Employees don't get the profile wizard; the effect above redirects them. Render nothing
  // meanwhile so the form never flashes.
  if (isEmployee) return null

  // Wait for fresh user data before deciding
  if (!allTestsPassed) return null

  // Profile already complete → the effect redirects to the report; don't flash the wizard.
  if (incompleteStep === null) return null

  return (
    <section className="flex flex-col gap-3">
      <div className="mx-auto flex w-full max-w-[480px] flex-col gap-5 px-4 py-6">
        {/* The profile wizard is an auth-gated, fully interactive form with no SSR value. Render it
            client-only so its inputs (notably the avatar file input) exist only once React is mounted
            and their event handlers are live — a server-rendered input can receive a `change` in the
            pre-hydration window and silently drop it (the avatar upload never registered).
            Start at the first step still missing data so prefilled (API/SDK) steps are skipped. */}
        <ClientOnly fallback={null}>
          <ApplicantFormSection
            user={user}
            initialData={initialData}
            callback={callback}
            initialStep={initialStep ?? incompleteStep}
          />
        </ClientOnly>
      </div>
    </section>
  )
}
